function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo, useRef, useState, useEffect } from 'react';
import { debounce, compact } from 'lodash-es';
import { PusherEventName } from 'anchor-scout-common/types/pusher';
import { TableList, Page, Button, PageEmptyState, PageErrorState, FilterPickerGroup, Searchbox, PageContentHeader, SingleSelectFilterPicker, useStateInRef, PaginationControl, Spinner, useOverlayContext } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { AccountingStatusFilter } from "../../business-logic/qboStatusFilterLogic";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { useIoContext, useStore, useStoreActions, useStoreStatic } from "../../contexts";
import { useDebouncedLogSearchToBI } from "../../hooks/useDebouncedLogSearchToBI";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useOnMount } from "../../hooks/useOnMount";
import { useRestfulWrapper } from "../../hooks/useRestfulWrapper";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { useSortFromUrl } from "../../hooks/useSortFromUrl";
import { handlePromise } from "../../infra/utils";
import { InvoiceOptimisticUpdateType, useSubscribeToInvoiceChangesObservable } from "../../providers/InvoiceChangesObservableProvider";
import { BusinessChannelEventBinder } from "../../pusher";
import { selectors } from "../../store/state-selectors";
import { useAppConfig } from "../../store/store-context";
import { getAndComparePayoutSummary } from "../../utils/payoutValidation";
import { slackClient } from "../../utils/slack-client";
import { CreateProposalButton } from "../../components/CreateProposalButton/CreateProposalButton";
import PageLoadingState from "../../components/PageLoadingState";
import SeeWhatItsLike from "../../components/SeeWhatItsLike/SeeWhatItsLike";
import PayoutsEmptyStateIcon from "./icons/payouts-empty-state.svg";
import styles from "./PayoutsDashboard.module.scss";
import { applyFilters, applySort, getAccountingStatusFilterOptions, getAccountingStatusFilterOptionsFromPayouts, mapHeaderIdToBackendField, getTableConfig } from "./PayoutsDashboard.utils";
import PayoutsDashboardRow from "./PayoutsDashboardRow/PayoutsDashboardRow";
import img1 from "./SeeWhatItsLikeImages/1.png";
import img2 from "./SeeWhatItsLikeImages/2.png";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const title = 'Payouts';
export function PayoutsDashboard(props) {
  const {
    data,
    isRefreshing,
    onFilterChange,
    onSortChange,
    urlState: externalUrlState,
    sort: externalSort,
    pageNumber,
    paginationPageSize,
    onPageNumberChange
  } = props;
  const {
    XERO_INTEGRATION = false,
    PAYOUT_VIEW = false,
    PAYOUTS_MONITOR_FE = false
  } = useFeatureFlags();
  const {
    api,
    logBI
  } = useStoreActions();
  const [sort, setSort] = useState({
    colIndex: 1,
    sort: 'desc'
  });
  const scrollElRef = useRef(null);
  const {
    openNewTab
  } = useIoContext();
  const {
    getState
  } = useStoreStatic();
  const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11401);
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
  const {
    downloadCsv
  } = useIoContext();
  const {
    exportCsvFilename
  } = useAppConfig();
  const {
    showErrorToast
  } = useOverlayContext();
  const [payouts, setPayouts] = useState(data.payouts);
  useEffect(() => {
    setPayouts(data.payouts);
    if (data.payouts.length > 0 && PAYOUTS_MONITOR_FE) {
      getAndComparePayoutSummary(api, getState, slackClient);
    }
  }, [data.payouts, api, PAYOUTS_MONITOR_FE]);
  const getPayouts = useStateInRef(payouts);
  const {
    urlState: internalUrlState,
    changeUrlState
  } = useStateFromUrl({
    qbo: {
      defaultValue: AccountingStatusFilter.All
    },
    xero: {
      defaultValue: AccountingStatusFilter.All
    },
    search: {
      defaultValue: ''
    },
    selected: {
      defaultValue: ''
    },
    payoutId: {
      defaultValue: ''
    }
  });
  const urlState = externalUrlState || internalUrlState;
  const {
    search,
    qbo: qboStatusFilter,
    xero: xeroStatusFilter,
    selected
  } = urlState;
  const currentSort = externalSort || sort;
  useOnMount(() => {
    if (selected) {
      const element = document.querySelector("[data-id=\"".concat(selected, "\"]"));
      element && requestAnimationFrame(() => element.scrollIntoView({
        block: 'center'
      }));
    }
  });
  useSubscribeToInvoiceChangesObservable(async _ref => {
    let {
      optimisticUpdate
    } = _ref;
    if (optimisticUpdate && (optimisticUpdate.type === InvoiceOptimisticUpdateType.QboIntegrationChanged || optimisticUpdate.type === InvoiceOptimisticUpdateType.XeroIntegrationChanged)) {
      const {
        invoiceId
      } = optimisticUpdate;
      const payoutToUpdate = getPayouts().find(p => p.invoices().some(i => i.id === invoiceId));
      if (!payoutToUpdate) return;
      const updatedPayout = await api.payout.getById(payoutToUpdate.id());
      setPayouts(prev => prev.map(prevPayout => prevPayout.id() === updatedPayout.id() ? updatedPayout : prevPayout));
    }
  });
  const updatePayoutFromPusher = payload => {
    var _scrollElRef$current;
    const currentScrollTop = ((_scrollElRef$current = scrollElRef.current) === null || _scrollElRef$current === void 0 ? void 0 : _scrollElRef$current.scrollTop) || 0;
    setPayouts(prev => prev.map(prevPayout => {
      if (prevPayout.id() === payload.payoutId) {
        return prevPayout.setIntegrations(payload.integrations);
      }
      return prevPayout;
    }));
    setTimeout(() => {
      if (scrollElRef.current) {
        scrollElRef.current.scrollTop = currentScrollTop;
      }
    }, 0);
  };
  const qboStatusFilterOptions = useMemo(() => {
    if (PAYOUT_VIEW && data.qboSyncStatusFilterCounters) {
      return getAccountingStatusFilterOptions(data.qboSyncStatusFilterCounters);
    }
    return getAccountingStatusFilterOptionsFromPayouts(applyFilters({
      payouts,
      qboStatusFilter: null,
      xeroStatusFilter: null,
      search
    }), payout => payout.isQboSynced());
  }, [PAYOUT_VIEW, data.qboSyncStatusFilterCounters, payouts, search]);
  const xeroStatusFilterOptions = useMemo(() => {
    if (PAYOUT_VIEW && data.xeroSyncStatusFilterCounters) {
      return getAccountingStatusFilterOptions(data.xeroSyncStatusFilterCounters);
    }
    return getAccountingStatusFilterOptionsFromPayouts(applyFilters({
      payouts,
      qboStatusFilter: null,
      xeroStatusFilter: null,
      search
    }), payout => payout.isXeroSynced());
  }, [PAYOUT_VIEW, data.xeroSyncStatusFilterCounters, payouts, search]);
  const {
    headerItems,
    columnDefs
  } = useMemo(() => getTableConfig({
    showQboIntegration,
    showXeroIntegration
  }), [showQboIntegration, showXeroIntegration]);
  const sortedAndFiltered = useMemo(() => {
    if (PAYOUT_VIEW) {
      return payouts;
    }
    const filtered = applyFilters({
      payouts,
      qboStatusFilter,
      xeroStatusFilter,
      search
    });
    const headerItemId = sort ? headerItems[sort.colIndex].id : null;
    return applySort(filtered, sort, headerItemId);
  }, [payouts, sort, search, qboStatusFilter, xeroStatusFilter, headerItems, PAYOUT_VIEW]);
  const {
    paginatedData,
    onScroll
  } = useInfiniteScroll({
    data: sortedAndFiltered,
    scrollElRef,
    alwaysRenderIndex: selected ? data => data.findIndex(p => p.id() === selected) : undefined
  });
  function handleSortChange(sort) {
    logBI(11400, {
      column: sort ? headerItems[sort.colIndex].value : 'none'
    });
    if (onSortChange) {
      onSortChange(sort);
    } else {
      setSort(sort);
    }
  }
  function onClearSearch() {
    if (onFilterChange) {
      onFilterChange({
        search: ''
      });
    } else {
      changeUrlState({
        search: ''
      });
    }
    debouncedLogSearchToBI.onClear();
  }
  async function exportToCsv() {
    if (PAYOUT_VIEW) {
      var _headerItems$currentS;
      const sortField = currentSort ? (_headerItems$currentS = headerItems[currentSort.colIndex]) === null || _headerItems$currentS === void 0 ? void 0 : _headerItems$currentS.id : undefined;
      const sortType = currentSort === null || currentSort === void 0 ? void 0 : currentSort.sort;
      const result = await handlePromise(api.payout.exportPayoutsToCsv({
        qboIntegration: qboStatusFilter,
        xeroIntegration: xeroStatusFilter,
        sort: sortField && sortType ? {
          field: mapHeaderIdToBackendField(sortField),
          type: sortType
        } : undefined,
        search: search
      }));
      if (result.success) {
        const filename = exportCsvFilename || 'exported-payouts.csv';
        downloadCsv(result.data, filename);
      } else {
        showErrorToast('Error exporting payouts to CSV', 'export-payouts-error');
      }
    } else {
      const mgmtHost = selectors.mgmtHost(getState());
      openNewTab("".concat(mgmtHost, "/business/export-payouts"));
    }
  }
  const hasPayoutsOrFiltersApplied = PAYOUT_VIEW ? payouts.length > 0 || search || qboStatusFilter !== AccountingStatusFilter.All || xeroStatusFilter !== AccountingStatusFilter.All : payouts.length > 0;
  return hasPayoutsOrFiltersApplied ? /*#__PURE__*/_jsxs(Page, {
    "data-testid": "payments-dashboard",
    ref: scrollElRef,
    header: title,
    actions: /*#__PURE__*/_jsx(Button, {
      "data-testid": "payments-export-csv",
      onClick: exportToCsv,
      children: "Export to CSV"
    }),
    subheader: "Review your paid invoices",
    onScroll: onScroll,
    children: [PAYOUT_VIEW && XERO_INTEGRATION && /*#__PURE__*/_jsx(BusinessChannelEventBinder, {
      source: "scout",
      eventName: PusherEventName.PayoutXeroIntegrationStatusUpdated,
      callback: updatePayoutFromPusher
    }), PAYOUT_VIEW && /*#__PURE__*/_jsx(BusinessChannelEventBinder, {
      source: "scout",
      eventName: PusherEventName.PayoutQboIntegrationStatusUpdated,
      callback: updatePayoutFromPusher
    }), /*#__PURE__*/_jsxs(TableList.Provider, {
      columnDefs: columnDefs,
      expandableRows: true,
      initialExpandedRows: selected ? {
        [selected]: true
      } : {},
      singleExpandOnly: true,
      sort: currentSort,
      onSortChange: handleSortChange,
      children: [/*#__PURE__*/_jsxs(PageContentHeader, {
        children: [/*#__PURE__*/_jsxs(FilterPickerGroup, {
          style: {
            marginBlockEnd: '24px'
          },
          children: [/*#__PURE__*/_jsx(Searchbox, {
            registerHotkey: true,
            value: search,
            "data-testid": "payments-dashboard-search",
            onChange: event => {
              const searchStr = event.target.value;
              if (onFilterChange) {
                onFilterChange({
                  search: searchStr
                }, {
                  withDebounce: true
                });
              } else {
                changeUrlState({
                  search: searchStr
                });
              }
              debouncedLogSearchToBI.onChange(searchStr);
            },
            onClear: onClearSearch
          }), showQboIntegration && qboStatusFilterOptions.length > 0 && /*#__PURE__*/_jsx(SingleSelectFilterPicker, {
            "data-testid": "payouts-qbo-filter",
            label: "QuickBooks Online sync status",
            value: qboStatusFilter,
            options: qboStatusFilterOptions,
            onChange: option => {
              if (option) {
                if (onFilterChange) {
                  onFilterChange({
                    qbo: option.value
                  });
                } else {
                  changeUrlState({
                    qbo: option.value
                  });
                }
              }
            }
          }), showXeroIntegration && xeroStatusFilterOptions.length > 0 && XERO_INTEGRATION && /*#__PURE__*/_jsx(SingleSelectFilterPicker, {
            "data-testid": "payouts-xero-filter",
            label: "Xero sync status",
            value: xeroStatusFilter,
            options: xeroStatusFilterOptions,
            onChange: option => {
              if (option) {
                if (onFilterChange) {
                  onFilterChange({
                    xero: option.value
                  });
                } else {
                  changeUrlState({
                    xero: option.value
                  });
                }
              }
            }
          })]
        }), /*#__PURE__*/_jsx(TableList.Header, {
          items: headerItems,
          "data-testid": "payments-dashboard-table-header"
        })]
      }), isRefreshing ? /*#__PURE__*/_jsx(Spinner, {
        centeredInBox: true,
        centerBoxHeight: 200
      }) : sortedAndFiltered.length ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [paginatedData.map((payout, index) => /*#__PURE__*/_jsx(PayoutsDashboardRow, {
          payout: payout,
          index: index,
          invoiceHeaders: data.invoiceHeaders
        }, payout.id())), PAYOUT_VIEW && /*#__PURE__*/_jsx("div", {
          className: styles.paginationControl,
          children: /*#__PURE__*/_jsx(PaginationControl, {
            pageSize: paginationPageSize || 100,
            pageNumber: pageNumber || 1,
            total: data.total,
            onPageNumberChange: onPageNumberChange || (() => {})
          })
        })]
      }) : /*#__PURE__*/_jsx(PageEmptyState, {
        illustration: /*#__PURE__*/_jsx(PayoutsEmptyStateIcon, {}),
        title: "No payouts match your request",
        message: "Try adjusting your search",
        actions: /*#__PURE__*/_jsx(Button, {
          "data-testid": "payments-dashboard-empty-search-clear-btn",
          onClick: onClearSearch,
          children: "Clear search"
        })
      })]
    })]
  }) : /*#__PURE__*/_jsx(Page, {
    header: title,
    loading: isRefreshing,
    children: /*#__PURE__*/_jsx(PageEmptyState, {
      "data-testid": "payments-empty",
      illustration: /*#__PURE__*/_jsx(PayoutsEmptyStateIcon, {}),
      title: "Automatic payments, with confidence",
      message: "Clients won't get any surprises at the end of the month. No manual collection or payment is needed \u2014 everything is managed by Anchor, automatically.",
      actions: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(SeeWhatItsLike, {
          steps: [{
            title: 'Cashflow - flows!',
            subtitle: 'During the proposal approval stage, your clients can choose how they’d like to pay.',
            image: {
              src: img1,
              width: 1198,
              height: 700
            }
          }, {
            title: 'Get paid on time',
            subtitle: 'Anchor will send reminders, handle payment processing, and ensure that late payments are a thing of the past!',
            image: {
              src: img2,
              width: 1246,
              height: 748
            }
          }],
          "data-testid": "payouts-dashboard"
        }), /*#__PURE__*/_jsx(CreateProposalButton, {
          hidePrefix: true,
          "data-testid": "payments-empty-new-btn"
        }, "create-proposal-button")]
      })
    })
  });
}
function MgmtPayoutsDashboard() {
  const payouts = useStore(state => state.payouts.data);
  const {
    getState
  } = useStoreStatic();
  const {
    fetchPayouts,
    fetchIntegrationsSummary
  } = useStoreActions();
  return /*#__PURE__*/_jsx(RestfulWrapper, {
    fetch: () => {
      const showQboIntegration = selectors.integration.showQboIntegration(getState());
      const showXeroIntegration = selectors.integration.showXeroIntegration(getState());
      if (showQboIntegration || showXeroIntegration) {
        fetchIntegrationsSummary();
      }
      return fetchPayouts();
    },
    initialData: payouts,
    renderChild: _ref2 => {
      let {
        data,
        loading
      } = _ref2;
      return /*#__PURE__*/_jsx(PayoutsDashboard, {
        data: data,
        isRefreshing: loading
      });
    },
    renderLoader: () => /*#__PURE__*/_jsx(PageLoadingState, {
      header: title,
      loading: true
    }),
    renderError: _ref3 => {
      let {
        refresh,
        error
      } = _ref3;
      return /*#__PURE__*/_jsx(PageErrorState, {
        title: title,
        error: error,
        onRefresh: () => refresh()
      });
    }
  });
}
function ScoutPayoutsDashboard() {
  const {
    paginationPageSize,
    inputDebounceTime
  } = useAppConfig();
  const {
    getState
  } = useStoreStatic();
  const {
    api,
    fetchIntegrationsSummary
  } = useStoreActions();
  const [pageNumber, _setPageNumber] = useState(1);
  const {
    headerItems
  } = useMemo(() => getTableConfig({
    showQboIntegration: selectors.integration.showQboIntegration(getState()),
    showXeroIntegration: selectors.integration.showXeroIntegration(getState())
  }), [getState]);
  const sortIds = compact(headerItems.map(item => item.id));
  const defaultSort = useMemo(() => ({
    colIndex: 1,
    sort: 'desc'
  }), []);
  const {
    sort,
    changeSort: _changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const {
    urlState,
    changeUrlState: _changeUrlState
  } = useStateFromUrl({
    qbo: {
      defaultValue: AccountingStatusFilter.All
    },
    xero: {
      defaultValue: AccountingStatusFilter.All
    },
    search: {
      defaultValue: ''
    },
    selected: {
      defaultValue: ''
    },
    payoutId: {
      defaultValue: ''
    }
  });
  const urlStateRef = useRef(urlState);
  const sortRef = useRef(sort);
  const pageNumberRef = useRef(pageNumber);
  const {
    renderRestfulState,
    refresh
  } = useRestfulWrapper({
    fetch: () => {
      var _headerItems$sortRef$, _sortRef$current;
      const showQboIntegration = selectors.integration.showQboIntegration(getState());
      const showXeroIntegration = selectors.integration.showXeroIntegration(getState());
      if (showQboIntegration || showXeroIntegration) {
        fetchIntegrationsSummary();
      }
      const sortField = sortRef.current ? (_headerItems$sortRef$ = headerItems[sortRef.current.colIndex]) === null || _headerItems$sortRef$ === void 0 ? void 0 : _headerItems$sortRef$.id : undefined;
      const sortType = (_sortRef$current = sortRef.current) === null || _sortRef$current === void 0 ? void 0 : _sortRef$current.sort;
      return api.payout.getAllFromScout({
        qboIntegration: urlStateRef.current.qbo !== AccountingStatusFilter.All ? urlStateRef.current.qbo : undefined,
        xeroIntegration: urlStateRef.current.xero !== AccountingStatusFilter.All ? urlStateRef.current.xero : undefined,
        search: urlStateRef.current.search || undefined,
        pageSize: paginationPageSize,
        pageNumber: pageNumberRef.current,
        sort: sortField && sortType ? {
          field: mapHeaderIdToBackendField(sortField),
          type: sortType
        } : undefined
      });
    }
  });
  useOnMount(() => {
    return () => refreshWithDebounce.cancel();
  });
  const refreshWithDebounce = useMemo(() => {
    const debounceTime = inputDebounceTime != null ? inputDebounceTime : 500;
    return debounce(refresh, debounceTime);
  }, [refresh, inputDebounceTime]);
  function changeUrlState(newUrlState) {
    _changeUrlState(newUrlState);
    urlStateRef.current = _objectSpread(_objectSpread({}, urlStateRef.current), newUrlState);
  }
  function changeSort(newSort) {
    _changeSort(newSort);
    sortRef.current = newSort;
  }
  function setPageNumber(pageNumber) {
    _setPageNumber(pageNumber);
    pageNumberRef.current = pageNumber;
  }
  function handleFilterChange(newUrlState, opts) {
    changeUrlState(newUrlState);
    setPageNumber(1);
    scheduleRefresh(opts);
  }
  function handleSortChange(sort) {
    changeSort(sort);
    setPageNumber(1);
    scheduleRefresh();
  }
  function handlePageNumberChange(pageNumber) {
    setPageNumber(pageNumber);
    scheduleRefresh();
  }
  function scheduleRefresh() {
    let {
      withDebounce
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    refreshWithDebounce();
    !withDebounce && refreshWithDebounce.flush();
  }
  return renderRestfulState({
    renderLoader: () => /*#__PURE__*/_jsx(PageLoadingState, {
      header: title,
      loading: true
    }),
    renderError: _ref4 => {
      let {
        refresh,
        error
      } = _ref4;
      return /*#__PURE__*/_jsx(PageErrorState, {
        title: title,
        error: error,
        onRefresh: () => refresh()
      });
    },
    renderChild: _ref5 => {
      let {
        data,
        loading
      } = _ref5;
      return /*#__PURE__*/_jsx(PayoutsDashboard, {
        data: data,
        isRefreshing: loading,
        onFilterChange: handleFilterChange,
        onSortChange: handleSortChange,
        onPageNumberChange: handlePageNumberChange,
        onResetFilters: () => handleFilterChange({
          qbo: AccountingStatusFilter.All,
          xero: AccountingStatusFilter.All,
          search: ''
        }),
        urlState: urlState,
        sort: sort,
        pageNumber: pageNumber,
        paginationPageSize: paginationPageSize
      });
    }
  });
}
export default function WrappedPayoutsDashboard() {
  const {
    PAYOUT_VIEW = false
  } = useFeatureFlags();
  useSetDocumentTitle(title);
  return PAYOUT_VIEW ? /*#__PURE__*/_jsx(ScoutPayoutsDashboard, {}) : /*#__PURE__*/_jsx(MgmtPayoutsDashboard, {});
}